/* ---------------------------------------------------------------------------
   RCDG — reinforced concrete deck girder (T-beam), simple span, interior or
   exterior.

   The family the DoR register actually needs: `RC T-beam, 3 girders` is what a
   Nepali highway bridge of this era is, and the imported cloud model says the
   same — deckType `girder`, three of them at 2.4 m.

   WHAT IT ADDS OVER RCFS, and each of the four is a real difference rather than
   a bigger version of the same thing:

   1. SHEAR IS RATED. RCFS computes a shear demand and never divides it; this
      family rates moment at midspan AND shear at the support, and in the
      reference workbook SHEAR GOVERNS — 0.29 against moment's 0.51. A
      transcription that carried over RCFS's moment-only shape would report a
      bridge as twice as strong as the workbook says it is.

   2. THE SECTION IS A T. The compression zone can be wholly inside the slab or
      spill into the web, and the two cases have different lever arms. The
      workbook resolves this with a circular cell the user retypes until it
      converges (correction C3); solved here in closed form — see `neutralAxis`.

   3. DAMAGE ACTS ON THREE THINGS, not one: section loss on the main steel, on
      the stirrups, and a shear-crack depth that reduces the concrete's shear
      contribution directly. All three are required inputs, none defaulted.

   4. THE GIRDER'S OWN SHARE OF THE DECK. An interior girder carries a full bay
      of slab; an exterior one carries half a bay plus its cantilever, its haunch
      and whatever the railing occupies. That is why `cantilever` is required for
      an exterior girder and refused for an interior one.

   THE EXTERIOR DISTRIBUTION FACTOR IS THE OPEN RULING (correction C11) and this
   is the family it bites. 1 + (S − 1.829)/S against (S − 1.829)/S is a factor of
   five on the live load of every exterior girder at S = 2.4 m. The profile
   carries the choice; nothing here decides it.
   --------------------------------------------------------------------------- */

import { excelRound } from './round.ts';
import {
  girderDistribution,
  liveLoadDemand,
  type Distribution,
  type LiveLoadDemand,
} from './demand.ts';
import { round2 } from './round.ts';
import type { RatingLevel, StandardsProfile } from './profile.ts';
import { allVehicles, rate, type Check, type RatingResult } from './rating.ts';
import type { MaterialValues } from './rcfs.ts';
import type { VehicleId } from './vehicles.ts';

export type GirderPosition = 'interior' | 'exterior';

/**
 * The deck an exterior girder carries beyond its half-bay.
 *
 * Required for an exterior girder and refused for an interior one — see
 * `rateRcdgSimple`. Every dimension in metres.
 */
export interface RcdgCantilever {
  /** Cantilever slab thickness at the tip, m. `LAYOUT!F33` on the exterior sheet. */
  thickness: number;
  /** Extra depth of the haunch where it meets the girder, m. `LAYOUT!F34`. */
  haunch: number;
  /** Length of the cantilever from the girder centreline, m. `LAYOUT!F35`. */
  length: number;
  /**
   * Width of railing and footway, m. `LAYOUT!F36`.
   *
   * Subtracted from the girder's deck width when the asphalt is worked out: a
   * footpath does not carry a wearing course. It is NOT subtracted from the
   * concrete, which is there either way.
   */
  railingWidth: number;
}

export interface RcdgInput {
  position: GirderPosition;

  /** Clear span, m. */
  span: number;
  /** Girder web width bw, m. `LAYOUT!F28`. */
  girderWidth: number;
  /** Girder web height below the slab soffit, h, m. `LAYOUT!F29`. */
  webHeight: number;
  /** Fillet/haunch width at the web–slab junction, m. `LAYOUT!F30`. */
  filletWidth: number;
  /** Fillet/haunch height, m. `LAYOUT!F31`. */
  filletHeight: number;
  /** Deck slab thickness ts, m. `LAYOUT!F32`. */
  slabThickness: number;
  /** Girder spacing S, m, on centres. `GENERAL!E16`. Also the slab width a girder carries. */
  girderSpacing: number;
  /**
   * How many girders the deck has. `GENERAL!E15`.
   *
   * Used only to share the two barrier rails across the girders — the workbook's
   * `2 x Wbr / nGirders`, which is a good deal cleaner than the flat slab's
   * unexplained 1.25 (correction C14).
   */
  girderCount: number;
  /** Weight of ONE barrier rail, kN/m. `GENERAL!H22`. */
  barrierRail: number;
  /** Wearing course thickness, MILLIMETRES. `GENERAL!E18`. */
  wearingCourse: number;
  concreteUnitWeight: number;
  asphaltUnitWeight: number;

  /**
   * Required for an exterior girder, and absent for an interior one.
   *
   * `null` and simply not being there mean the same thing and are both accepted
   * — the API writes the field explicitly, the browser's form omits it.
   */
  cantilever?: RcdgCantilever | null;

  /** Main tension reinforcement over the girder, mm2, before section loss. */
  steelArea: number;
  /** Centroid of that steel above the girder soffit, mm. */
  centroidFromBottom: number;
  /** Loss of main reinforcement to corrosion, per cent. */
  sectionLoss: number;

  /** Area of ONE stirrup leg pair's bar, mm2. `CCLFM!J12`. */
  stirrupArea: number;
  /** Stirrup spacing along the span, mm. */
  stirrupSpacing: number;
  /** Loss of stirrup cross-section to exposure, per cent. */
  stirrupSectionLoss: number;
  /**
   * Depth of shear cracking into the web, mm. `CCLFM!H34`.
   *
   * Reduces the concrete's shear contribution by `1 − depth/bw`, and takes it to
   * zero once the crack has crossed half the web. Zero is a real value and means
   * inspected and none found.
   */
  shearCrackDepth: number;

  concrete: MaterialValues;
  rebar: MaterialValues;
}

/* --------------------------- dead load --------------------------- */

export interface RcdgDeadLoad {
  /** Girder web, kN/m. */
  girder: number;
  /** The two fillets at the web–slab junction, kN/m. */
  fillet: number;
  /** Deck slab this girder carries, kN/m. A full bay inside, half a bay outside. */
  slab: number;
  /** Cantilever slab and its haunch, kN/m. Zero for an interior girder. */
  cantilever: number;
  /** This girder's share of the two barrier rails, kN/m. */
  barrier: number;
  /** Asphalt over the width this girder carries, kN/m. */
  overlay: number;
  /** The sum, rounded to two decimals and carried forward — correction C10. */
  total: number;
  /** The same unrounded. */
  totalExact: number;
  /** Moment at midspan, kN-m. */
  moment: number;
  /** Shear at the support, kN. */
  shear: number;
}

/**
 * Reproduces `DFDLLL!J16:J23` (interior) and `DFDLLL!J21:J29` (exterior).
 *
 * A SEPARATE FUNCTION FROM `deadLoad` IN demand.ts, which is the flat slab's.
 * The two look similar and are not: this one is per girder rather than per metre
 * of width, counts a web and its fillets, and shares the barrier by girder count
 * rather than spreading it over the deck. Folding them together would need a
 * parameter for every one of those differences.
 */
export function rcdgDeadLoad(input: RcdgInput): RcdgDeadLoad {
  const wu = input.concreteUnitWeight;

  const girder = input.girderWidth * input.webHeight * wu;
  /* Two fillets, each a triangle: 2 x 1/2 x wf x hf x Wu. */
  const fillet = 2 * 0.5 * input.filletWidth * input.filletHeight * wu;

  /* An interior girder carries a full bay of slab; an exterior one carries half
     the bay between it and its neighbour, measured from the web face. */
  const slab =
    input.position === 'interior'
      ? input.slabThickness * input.girderSpacing * wu
      : 0.5 * (input.girderSpacing - input.girderWidth) * input.slabThickness * wu;

  const cant = input.cantilever ?? null;
  /* 1/2 x (2 x thickness + haunch) x length x Wu — a trapezoid deepening towards
     the girder, written exactly as the sheet writes it. */
  const cantilever =
    cant === null ? 0 : 0.5 * (2 * cant.thickness + cant.haunch) * cant.length * wu;

  /* Two rails shared over every girder. */
  const barrier = (2 * input.barrierRail) / input.girderCount;

  /* The width of deck this girder carries asphalt over. Inside, the full bay.
     Outside, half a bay plus half a web plus the cantilever, less whatever the
     railing and footway occupy — which carry no wearing course. */
  const overlayWidth =
    cant === null
      ? input.girderSpacing
      : input.girderSpacing / 2 + input.girderWidth / 2 + cant.length - cant.railingWidth;
  const overlay = (input.wearingCourse / 1000) * overlayWidth * input.asphaltUnitWeight;

  const totalExact = girder + fillet + slab + cantilever + barrier + overlay;
  const total = round2(totalExact);

  return {
    girder,
    fillet,
    slab,
    cantilever,
    barrier,
    overlay,
    total,
    totalExact,
    moment: (input.span * input.span * total) / 8,
    shear: (input.span * total) / 2,
  };
}

/* --------------------------- geometry --------------------------- */

/** Effective depth to the main steel from the top of the slab, mm. */
export function effectiveDepth(input: RcdgInput): number {
  return (input.webHeight + input.slabThickness) * 1000 - input.centroidFromBottom;
}

export interface EffectiveWidth {
  /** The governing beff, mm. */
  value: number;
  /** 12ts + bw, mm. */
  twelveSlabs: number;
  /** L/4, mm. */
  quarterSpan: number;
  /** The girder spacing, mm. */
  spacing: number;
  /** Which of the three governed, for the report. */
  governedBy: '12ts + bw' | 'L/4' | 'girder spacing';
}

/** `CCLFM!H28:H30` — the least of the three AASHTO limits on a T-beam flange. */
export function effectiveWidth(input: RcdgInput): EffectiveWidth {
  const twelveSlabs = (12 * input.slabThickness + input.girderWidth) * 1000;
  const quarterSpan = (input.span / 4) * 1000;
  const spacing = input.girderSpacing * 1000;
  const value = Math.min(twelveSlabs, quarterSpan, spacing);
  const governedBy =
    value === spacing ? 'girder spacing' : value === twelveSlabs ? '12ts + bw' : 'L/4';
  return { value, twelveSlabs, quarterSpan, spacing, governedBy };
}

/**
 * The reduction on the concrete's shear capacity from web cracking.
 *
 * `1 − depth/bw`, and zero once the crack has reached half the web — at which
 * point the workbook stops crediting the concrete with any shear capacity at
 * all. `CCLFM!H35`.
 */
export function crackReduction(crackDepth: number, webWidth: number): number {
  return crackDepth / webWidth >= 0.5 ? 0 : 1 - crackDepth / webWidth;
}

/* --------------------------- capacity, LFM --------------------------- */

export interface RcdgLfmCapacity {
  steelArea: number;
  stirrupArea: number;
  depth: number;
  effectiveWidth: EffectiveWidth;
  /**
   * Stress block depth a, mm, ROUNDED TO ONE DECIMAL.
   *
   * One decimal here and a WHOLE MILLIMETRE on the flat slab (`CCLFM!J33`
   * against `CCLFM!J44`). Two workbooks in the same program rounding the same
   * quantity to different precisions is not a decision anybody took; it is
   * reproduced because it is on the reference path. Correction C10.
   */
  stressBlock: number;
  stressBlockExact: number;
  /** Ultimate moment capacity at midspan, kN-m. */
  moment: number;

  /** Concrete's shear contribution before cracking is allowed for, kN. */
  shearConcreteFull: number;
  /** The reduction factor applied to it. */
  crackReduction: number;
  /** Concrete's shear contribution as reduced, kN. */
  shearConcrete: number;
  /** Stirrups' shear contribution, kN. */
  shearSteel: number;
  /** Total factored shear capacity at the support, kN. */
  shear: number;
}

export function rcdgLfmCapacity(input: RcdgInput, profile: StandardsProfile): RcdgLfmCapacity {
  const steelArea = (1 - input.sectionLoss / 100) * input.steelArea;
  const stirrupArea = (1 - input.stirrupSectionLoss / 100) * input.stirrupArea;
  const depth = effectiveDepth(input);
  const beff = effectiveWidth(input);
  const bw = input.girderWidth * 1000;

  const stressBlockExact =
    (steelArea * input.rebar.strength) / (0.85 * input.concrete.strength * beff.value);
  const stressBlock = excelRound(stressBlockExact, 1);

  const moment =
    (profile.capacityFactor * steelArea * input.rebar.strength * (depth - stressBlock / 2)) / 1e6;

  const reduction = crackReduction(input.shearCrackDepth, bw);
  const shearConcreteFull = (0.167 * Math.sqrt(input.concrete.strength) * bw * depth) / 1000;
  const shearConcrete = reduction * shearConcreteFull;
  /* Two legs: 2 x Av x fy x d / S. */
  const shearSteel = (2 * stirrupArea * input.rebar.strength * depth) / input.stirrupSpacing / 1000;
  const shear = (shearConcrete + shearSteel) * profile.shearCapacityFactor;

  return {
    steelArea,
    stirrupArea,
    depth,
    effectiveWidth: beff,
    stressBlock,
    stressBlockExact,
    moment,
    shearConcreteFull,
    crackReduction: reduction,
    shearConcrete,
    shearSteel,
    shear,
  };
}

/* --------------------------- the neutral axis --------------------------- */

export interface NeutralAxis {
  /** Depth of the neutral axis below the top of the slab, mm. */
  kd: number;
  /** Whether the compression zone stays inside the slab. */
  withinSlab: boolean;
  /** Depth into the WEB below the slab, mm. Zero where the zone stays in the slab. */
  webDepth: number;
  /** k, as the sheet reports it — kd/d, which is the rectangular k only when the zone is in the slab. */
  k: number;
  /** The centroid of the compression zone below the top of the slab, mm. `CCASM!H25`. */
  z: number;
  /** The lever arm d − z, mm. */
  jd: number;
}

/**
 * Where the neutral axis sits, solved rather than iterated.
 *
 * THIS IS CORRECTION C3. The workbook makes the user retype `yinput` from
 * `ycalculate` until the two agree, and records nothing about whether they ever
 * did — so an unconverged sheet reads exactly like a converged one. The loop is
 * a quadratic and is solved here in one step.
 *
 * The workbook's own relation, at `CCASM!B34`, is
 *
 *     ts + y = (n·As·d + ½·b·ts² + bw·y·(y/2 + ts)) / (n·As + b·ts + bw·y)
 *
 * Multiplying out and collecting in y gives
 *
 *     (bw/2)·y² + (n·As + b·ts)·y + [n·As·(ts − d) + ½·b·ts²] = 0
 *
 * A NEGATIVE ROOT IS THE ANSWER, NOT A FAILURE: it says the compression zone
 * never reaches the web, and the section is rectangular after all. The reference
 * bridge is exactly that case — y solves to −19.4 mm, and the sheet takes the
 * rectangular k·d of 159.8 mm against a 180 mm slab. Both branches are
 * reproduced because a deeper girder or a thinner slab takes the other one.
 */
export function neutralAxis(input: RcdgInput, profile: StandardsProfile): NeutralAxis {
  const steelArea = (1 - input.sectionLoss / 100) * input.steelArea;
  const depth = effectiveDepth(input);
  const b = effectiveWidth(input).value;
  const bw = input.girderWidth * 1000;
  const ts = input.slabThickness * 1000;
  const n = profile.modularRatio;

  /* The rectangular result first, which is what the sheet tests against. */
  const rho = steelArea / depth / b;
  const rhoN = rho * n;
  const kRect = Math.sqrt(2 * rhoN + rhoN * rhoN) - rhoN;
  const kdRect = kRect * depth;

  let kd = kdRect;
  let webDepth = 0;
  if (kdRect > ts) {
    /* The compression zone spills into the web. Solve for y. */
    const A = n * steelArea;
    const B = b * ts;
    const qa = bw / 2;
    const qb = A + B;
    const qc = A * (ts - depth) + 0.5 * b * ts * ts;
    const disc = qb * qb - 4 * qa * qc;
    /* A negative discriminant would mean no real neutral axis, which a section
       with steel in it cannot have. Falling back to the slab depth rather than
       producing NaN: the caller gets a number it can see is wrong, not a
       capacity that silently reads as zero. */
    const y = disc < 0 ? 0 : (-qb + Math.sqrt(disc)) / (2 * qa);
    webDepth = Math.max(0, y);
    /* Floored at ts, as `CCASM!B34` floors it. */
    kd = Math.max(ts, ts + webDepth);
  }

  const withinSlab = kd <= ts;

  /* The centroid of the compression zone, as three areas and their lever arms —
     `CCASM!B63:D66`. Within the slab the first two vanish and z is kd/3. */
  const areaWeb = kd > ts ? (0.5 * (kd - ts) * (kd - ts)) / kd : 0;
  const armWeb = ts + (kd - ts) / 3;
  const areaSlabLower = kd > ts ? (ts * (kd - ts)) / kd : 0;
  const armSlabLower = ts / 2;
  const areaSlabUpper = kd > ts ? (0.5 * ts * ts) / kd : 0.5 * kd;
  const armSlabUpper = kd > ts ? ts / 3 : kd / 3;

  const areaTotal = areaWeb + areaSlabLower + areaSlabUpper;
  const momentTotal = areaWeb * armWeb + areaSlabLower * armSlabLower + areaSlabUpper * armSlabUpper;
  const z = areaTotal === 0 ? 0 : momentTotal / areaTotal;

  return { kd, withinSlab, webDepth, k: kd / depth, z, jd: depth - z };
}

/* --------------------------- capacity, ASM --------------------------- */

/**
 * The ASM shear stress coefficients, by level.
 *
 * `0.08√f'c` at inventory and `0.12√f'c` at operating — `CCASM!H41:H42`. Kept
 * here rather than in the profile because they are the method's definition of an
 * allowable shear stress rather than a departmental calibration, which is the
 * same line `IMPACT_CAP` sits on in vehicles.ts.
 */
export const ASM_SHEAR_COEFFICIENT: Readonly<Record<RatingLevel, number>> = {
  inventory: 0.08,
  operating: 0.12,
};

export interface RcdgAsmCapacity {
  neutralAxis: NeutralAxis;
  concreteMoment: Readonly<Record<RatingLevel, number>>;
  rebarMoment: Readonly<Record<RatingLevel, number>>;
  moment: Readonly<Record<RatingLevel, number>>;
  governedBy: Readonly<Record<RatingLevel, 'concrete' | 'reinforcement'>>;
  shearConcrete: Readonly<Record<RatingLevel, number>>;
  shearSteel: Readonly<Record<RatingLevel, number>>;
  /** Vc + Vs. NO capacity factor — the ASM factors are unity throughout. */
  shear: Readonly<Record<RatingLevel, number>>;
}

export function rcdgAsmCapacity(input: RcdgInput, profile: StandardsProfile): RcdgAsmCapacity {
  const steelArea = (1 - input.sectionLoss / 100) * input.steelArea;
  const stirrupArea = (1 - input.stirrupSectionLoss / 100) * input.stirrupArea;
  const depth = effectiveDepth(input);
  const b = effectiveWidth(input).value;
  const bw = input.girderWidth * 1000;
  const axis = neutralAxis(input, profile);
  const reduction = crackReduction(input.shearCrackDepth, bw);

  /* The three compression areas again, weighted by the width each acts over —
     the web for the part below the slab, the flange for the parts above it. */
  const kd = axis.kd;
  const ts = input.slabThickness * 1000;
  const areaWeb = kd > ts ? (0.5 * (kd - ts) * (kd - ts)) / kd : 0;
  const areaSlabLower = kd > ts ? (ts * (kd - ts)) / kd : 0;
  const areaSlabUpper = kd > ts ? (0.5 * ts * ts) / kd : 0.5 * kd;
  const compression = bw * areaWeb + b * areaSlabLower + b * areaSlabUpper;

  const forLevel = <T>(fn: (level: RatingLevel) => T): Readonly<Record<RatingLevel, T>> => ({
    inventory: fn('inventory'),
    operating: fn('operating'),
  });

  const concreteMoment = forLevel(
    (level) => (compression * input.concrete.allowable[level] * axis.jd) / 1e6,
  );
  const rebarMoment = forLevel(
    (level) => (steelArea * input.rebar.allowable[level] * axis.jd) / 1e6,
  );
  const shearConcrete = forLevel(
    (level) =>
      (ASM_SHEAR_COEFFICIENT[level] * Math.sqrt(input.concrete.strength) * bw * depth * reduction) /
      1000,
  );
  const shearSteel = forLevel(
    (level) => (2 * stirrupArea * input.rebar.allowable[level] * depth) / input.stirrupSpacing / 1000,
  );

  return {
    neutralAxis: axis,
    concreteMoment,
    rebarMoment,
    moment: forLevel((level) => Math.min(concreteMoment[level], rebarMoment[level])),
    governedBy: forLevel((level) =>
      concreteMoment[level] < rebarMoment[level] ? 'concrete' : 'reinforcement',
    ),
    shearConcrete,
    shearSteel,
    shear: forLevel((level) => shearConcrete[level] + shearSteel[level]),
  };
}

/* --------------------------- the whole rating --------------------------- */

export interface RcdgRating {
  family: 'RCDG';
  continuity: 'simple';
  position: GirderPosition;
  distribution: Distribution;
  dead: RcdgDeadLoad;
  live: readonly LiveLoadDemand[];
  lfm: RcdgLfmCapacity;
  asm: RcdgAsmCapacity;
  result: RatingResult;
}

/**
 * Rate a simple-span RC deck girder.
 *
 * FOUR CHECKS, not two: moment and shear, each under both methods. The governing
 * value is the least of them, and on the reference bridge that is ASM shear at
 * 0.235 — a long way below the moment rating anybody would quote from RCFS's
 * shape.
 */
export function rateRcdgSimple(input: RcdgInput, profile: StandardsProfile): RcdgRating {
  /* Refused rather than ignored. An interior girder with a cantilever, or an
     exterior one without, is a request describing a different bridge from the
     one being rated — and the dead load would be wrong by several kN/m either
     way with nothing on the report to show it. */
  /* `?? null` rather than a bare null comparison. Absent and null are the same
     claim — the girder has no cantilever — and they arrive differently: the API
     sets the field explicitly, while the browser's assembler simply never writes
     a key the form did not ask for. Comparing against null alone read an omitted
     field as `undefined !== null` and refused every interior girder rated from
     the form, while the same input through the API was accepted. */
  const cantilever = input.cantilever ?? null;
  if (input.position === 'exterior' && cantilever === null) {
    throw new Error('An exterior girder needs its cantilever dimensions.');
  }
  if (input.position === 'interior' && cantilever !== null) {
    throw new Error('An interior girder carries no cantilever.');
  }

  const dead = rcdgDeadLoad(input);
  const distribution = girderDistribution(
    input.girderSpacing,
    input.position === 'interior' ? 'interior-girder' : 'exterior-girder',
    profile.exteriorDf,
  );
  const live = allVehicles(profile).map((v) => liveLoadDemand(v.id, input.span, distribution));

  const lfm = rcdgLfmCapacity(input, profile);
  const asm = rcdgAsmCapacity(input, profile);

  const liveMoments: Partial<Record<VehicleId, number>> = {};
  const liveShears: Partial<Record<VehicleId, number>> = {};
  for (const demand of live) {
    liveMoments[demand.vehicle] = demand.moment;
    liveShears[demand.vehicle] = demand.shear;
  }

  const checks: Check[] = [
    {
      id: 'moment-midspan',
      label: 'Moment (midspan)',
      method: 'LFM',
      capacity: { inventory: lfm.moment, operating: lfm.moment },
      dead: dead.moment,
      live: liveMoments,
    },
    {
      id: 'shear-support',
      label: 'Shear (support)',
      method: 'LFM',
      capacity: { inventory: lfm.shear, operating: lfm.shear },
      dead: dead.shear,
      live: liveShears,
      ...(lfm.crackReduction < 1
        ? { governedBy: `web cracking, concrete reduced to ${Math.round(lfm.crackReduction * 100)}%` }
        : {}),
    },
    {
      id: 'moment-midspan',
      label: 'Moment (midspan)',
      method: 'ASM',
      capacity: asm.moment,
      dead: dead.moment,
      live: liveMoments,
      governedBy: `${asm.governedBy.inventory} allowable stress`,
    },
    {
      id: 'shear-support',
      label: 'Shear (support)',
      method: 'ASM',
      capacity: asm.shear,
      dead: dead.shear,
      live: liveShears,
      ...(lfm.crackReduction < 1
        ? { governedBy: `web cracking, concrete reduced to ${Math.round(lfm.crackReduction * 100)}%` }
        : {}),
    },
  ];

  return {
    family: 'RCDG',
    continuity: 'simple',
    position: input.position,
    distribution,
    dead,
    live,
    lfm,
    asm,
    result: rate(checks, profile),
  };
}
